/**
 * Was auf Zuhause JETZT gilt — CLI, Extension, wer wirklich antwortet.
 *
 * 2026-09-01 (Martin): Die Infos lagen in Einstellungen und in der Fusszeile.
 * Auf Zuhause fehlten sie, genau dann, wenn Claude tot ist und Gemini
 * einspringt. Eine Quelle: dieselben Endpunkte wie Settings, kein zweiter
 * Wahrheitsweg.
 */
import { loadSettings } from "./settings.js";
import { fetchExtensionStatus, type ExtensionStatus } from "./connectors-api.js";
import { istHauptnutzer } from "./hauptnutzer.js";
import { aktuelleBackup, nameFuerId } from "./settings/modell-status.js";

type LageZustand = "aktiv" | "warn" | "aus";

export interface LageZeile {
  name: string;
  wert: string;
  zustand: LageZustand;
  /** Ticket 2264-Nachtrag (14.09.2026): optional — nicht jede Zeile hat eine
   *  passende Einstellungsseite (z.B. "Im Hintergrund"). Ohne ziel ist die
   *  Zeile nicht anklickbar, nur informativ. */
  ziel?: "bot" | "connectors";
}

interface LaufzeitKette {
  id?: string;
  name?: string;
  status?: string;
  hinweis?: string;
}

interface LaufzeitAntwort {
  id?: string;
  fallback?: boolean;
  /** Kundenkonto: Gemini im Paket ist der Normalfall (Ticket 2779). */
  kunde?: boolean;
  grund?: string | null;
  kette?: LaufzeitKette[];
}

export function lageAus(opts: {
  modell: LaufzeitAntwort | null;
  cli: { ok?: boolean; detail?: string } | null;
  ext: ExtensionStatus | null;
  cliChat: boolean;
}): LageZeile[] {
  const claude = opts.modell?.kette?.find((s) => /claude/i.test(s.id ?? ""));
  const claudeStatus = claude?.status ?? "";

  const modellName = nameFuerId(opts.modell?.id ?? "");
  const antwortet: LageZeile = {
    name: "Antwortet",
    wert: opts.modell?.grund
      ? `${modellName} · ${opts.modell.grund}`
      : opts.modell?.fallback
        ? `${modellName} · Ersatz`
        : `${modellName} · aktiv`,
    zustand:
      opts.modell?.fallback && !opts.modell?.kunde
        ? "warn"
        : opts.modell?.id
          ? "aktiv"
          : "aus",
    ziel: "bot",
  };

  let cli: LageZeile;
  if (!opts.cliChat) {
    cli = {
      name: "Claude CLI",
      wert: "ausgeschaltet",
      zustand: "aus",
      ziel: "bot",
    };
  } else if (claudeStatus === "tot") {
    cli = {
      name: "Claude CLI",
      wert: claude?.hinweis || "nicht erreichbar",
      zustand: "warn",
      ziel: "bot",
    };
  } else if (claudeStatus === "aktiv") {
    cli = {
      name: "Claude CLI",
      wert: "aktiv",
      zustand: "aktiv",
      ziel: "bot",
    };
  } else if (claudeStatus === "bereit" || opts.cli?.ok) {
    cli = {
      name: "Claude CLI",
      wert: "bereit",
      zustand: "aktiv",
      ziel: "bot",
    };
  } else {
    cli = {
      name: "Claude CLI",
      wert: opts.cli?.detail || "nicht erreichbar",
      zustand: "aus",
      ziel: "bot",
    };
  }

  let ext: LageZeile;
  if (opts.ext?.connected) {
    ext = {
      name: "Chrome-Extension",
      wert: "aktiv",
      zustand: "aktiv",
      ziel: "connectors",
    };
  } else if (opts.ext?.paired) {
    ext = {
      name: "Chrome-Extension",
      wert: "gekoppelt, nicht aktiv",
      zustand: "warn",
      ziel: "connectors",
    };
  } else {
    ext = {
      name: "Chrome-Extension",
      wert: "nicht verbunden",
      zustand: "aus",
      ziel: "connectors",
    };
  }

  // Ticket 2779: Das Claude-Abo ist das des Betreibers — für ein Kundenkonto
  // antwortet es nie, also steht es dort nicht als „bereit“.
  return opts.modell?.kunde ? [antwortet, ext] : [antwortet, cli, ext];
}

interface LageStueck {
  label: string;
  zustand: LageZustand;
  /** Laufende Arbeit atmet — siehe lageKopf(). */
  pulsiert?: boolean;
}

/**
 * Eine Zeile für den Kopf — nicht drei Karten.
 *
 * Ticket 2264-Nachtrag (14.09.2026, Martin live: "vielleicht auch irgendwo
 * vermerken, dass im Hintergrund Verarbeitungen stattfinden, dass man noch
 * bessere Transparenz hat, so wie eine Art Monitoring"): eine vierte,
 * optionale Zeile für Hintergrund-Arbeit (siehe hintergrundZeile()). Sie
 * erscheint im KOPF nur, wenn wirklich etwas läuft — sonst würde die sonst
 * stille Randzeile ("nur peripher wichtig", Martin 05.09.) staendig einen
 * Punkt zeigen und genau die Ruhe verlieren, die dort gewollt war. In der
 * aufgeklappten Liste steht sie immer, auch im Leerlauf ("Nichts läuft").
 */
export function lageKopf(
  zeilen: LageZeile[],
  /**
   * Zeigt der Kopf auch die Innereien? (16.09.2026)
   *
   * ⚠️ Martin sieht auf Alberts Seite *„Sonnet · CLI an · Extension aus"* und
   *    sagt: *„Bi kann er gar nicht nutzen."* Er hat recht — welches Modell
   *    gerade antwortet und ob der CLI laeuft, kann ein Kunde weder
   *    verstehen noch aendern. Es ist Entwicklungs-Auskunft.
   *
   *    Was BLEIBT, ist das, woran er etwas tun oder ablesen kann: die
   *    Extension (die koennte er installieren) und die Hintergrund-Arbeit
   *    („Ton wird verarbeitet") — die sagt ihm, dass gerade etwas fuer ihn
   *    laeuft, statt dass er auf ein stilles Bild schaut.
   *
   * ⚠️ Vorgabe FALSE: wer die Angabe vergisst, zeigt zu wenig, nie zu viel.
   */
  mitInnereien = false,
): LageStueck[] {
  const [antwortet, cli, ext, hintergrund] = zeilen;
  const modell = (antwortet?.wert ?? "").split(" · ")[0]?.trim() || "unbekannt";
  const cliWort =
    cli?.zustand === "aktiv" ? "CLI an" : cli?.zustand === "warn" ? "CLI tot" : "CLI aus";
  const extWort =
    ext?.zustand === "aktiv"
      ? "Extension an"
      : ext?.zustand === "warn"
        ? "Extension wartet"
        : "Extension aus";
  const stuecke: LageStueck[] = mitInnereien
    ? [
        { label: modell, zustand: antwortet?.zustand ?? "aus" },
        { label: cliWort, zustand: cli?.zustand ?? "aus" },
        { label: extWort, zustand: ext?.zustand ?? "aus" },
      ]
    : [{ label: extWort, zustand: ext?.zustand ?? "aus" }];
  if (hintergrund?.zustand === "aktiv") {
    stuecke.push({
      label: hintergrund.wert,
      zustand: "aktiv",
      /*
       * Martin: *„Dann sollte der vielleicht leicht pulsieren, damit man
       * weiss, dass da was passiert."* Ein Punkt, der steht, sieht aus wie
       * jeder andere Zustand; ein Punkt, der atmet, sagt: es laeuft gerade.
       */
      pulsiert: true,
    });
  }
  return stuecke;
}


function punktKlasse(z: LageZustand): string {
  return (
    "connector-dot " +
    (z === "aktiv" ? "is-ok" : z === "warn" ? "is-warn" : "is-off")
  );
}

/**
 * Ticket 2264-Nachtrag (14.09.2026): Übersetzung der rohen Gründe aus
 * `/api/system/busy` (server/index.ts) in kurze, verständliche Worte. Die
 * Server-Seite ist die EINE Wahrheit über WAS läuft (dasselbe Tor, das auch
 * einen Neustart aufhält) — hier steht nur, WIE es Martin genannt wird.
 *
 * Martin fragte live, was die fast 100% CPU verbraucht (Antwort: die
 * Sprecher-Erkennung, `sprecher-lauf`) — genau dieser Fall ist der Grund für
 * diese Zeile.
 */
const HINTERGRUND_LABEL: Record<string, string> = {
  "sprecher-lauf": "Sprecher-Erkennung",
  "sprecher-warteschlange": "Sprecher-Erkennung wartet",
  "chat-zug": "Antwort wird gedacht",
  "audio-stream": "Ton wird verarbeitet",
  "vorlesen-nachlauf": "Vorlesen läuft nach",
  tasks: "Hintergrund-Auftrag läuft",
  "aufnahme-aktiv": "Tagesaufnahme läuft",
  aufnahme: "Aufnahme läuft",
  "recorder-finish": "Aufnahme wird abgeschlossen",
  anruf: "Anruf läuft",
};

export function hintergrundZeile(info: {
  busy: boolean;
  reasons: string[];
}): LageZeile {
  const teile = Array.from(
    new Set(
      (info.reasons ?? []).map((r) => {
        const praefix = r.split(":")[0] ?? r;
        return HINTERGRUND_LABEL[praefix] ?? praefix;
      }),
    ),
  );
  return {
    name: "Im Hintergrund",
    wert: info.busy && teile.length > 0 ? teile.join(" · ") : "Nichts läuft gerade",
    zustand: info.busy && teile.length > 0 ? "aktiv" : "aus",
  };
}

export async function holeZuhauseLage(): Promise<LageZeile[]> {
  const s = loadSettings();
  const wahl = s.modellBevorzugt === "gemini" ? "gemini" : "auto";
  const backup = aktuelleBackup();
  const [modell, cli, ext, busy] = await Promise.all([
    fetch(
      `/api/runtime-modell?wahl=${encodeURIComponent(wahl)}&backup=${encodeURIComponent(backup)}`,
      {
        credentials: "include",
      },
    )
      .then((r) => (r.ok ? (r.json() as Promise<LaufzeitAntwort>) : null))
      .catch(() => null),
    fetch("/api/cli/health", { credentials: "same-origin" })
      .then((r) =>
        r.ok ? (r.json() as Promise<{ ok?: boolean; detail?: string }>) : null,
      )
      .catch(() => null),
    fetchExtensionStatus().catch(() => null),
    fetch("/api/system/busy", { credentials: "same-origin" })
      .then((r) =>
        r.ok
          ? (r.json() as Promise<{ busy?: boolean; reasons?: string[] }>)
          : null,
      )
      .catch(() => null),
  ]);
  return [
    ...lageAus({
      modell,
      cli,
      ext,
      cliChat: s.cliChat !== false,
    }),
    hintergrundZeile({ busy: !!busy?.busy, reasons: busy?.reasons ?? [] }),
  ];
}

export function zeichneZuhauseLage(
  root: HTMLElement,
  zeilen: LageZeile[],
): void {
  const slot = root.querySelector<HTMLElement>("[data-slot=lage]");
  if (!slot) return;
  const warOffen = slot instanceof HTMLDetailsElement && slot.open;
  slot.replaceChildren();
  const kopf = document.createElement("summary");
  kopf.className = "zuhause-lage-kopf";
  for (const s of lageKopf(zeilen, istHauptnutzer())) {
    const stueck = document.createElement("span");
    stueck.className = "zuhause-lage-stueck";
    stueck.dataset.zustand = s.zustand;
    if (s.pulsiert) stueck.dataset.laeuft = "ja";
    const punkt = document.createElement("span");
    punkt.className = punktKlasse(s.zustand);
    punkt.setAttribute("aria-hidden", "true");
    stueck.append(punkt, document.createTextNode(s.label));
    kopf.append(stueck);
  }
  const liste = document.createElement("ul");
  liste.className = "zuhause-lage-mehr";
  for (const z of zeilen) {
    const li = document.createElement("li");
    li.tabIndex = 0;
    li.setAttribute("role", "button");
    const name = document.createElement("span");
    name.className = "zuhause-lage-name";
    name.textContent = z.name;
    const wert = document.createElement("span");
    wert.className = "zuhause-lage-wert";
    wert.textContent = z.wert;
    li.append(name, wert);
    // Ticket 2264-Nachtrag: Zeilen ohne ziel (z.B. "Im Hintergrund") haben
    // keine passende Einstellungsseite — rein informativ, nicht anklickbar.
    if (z.ziel) {
      const ziel = z.ziel;
      const oeffne = (): void => {
        document.dispatchEvent(
          new CustomEvent("vespera:go-settings", { detail: { sub: ziel } }),
        );
      };
      li.tabIndex = 0;
      li.setAttribute("role", "button");
      li.addEventListener("click", oeffne);
      li.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          oeffne();
        }
      });
    }
    liste.append(li);
  }
  slot.append(kopf, liste);
  if (slot instanceof HTMLDetailsElement) slot.open = warOffen;
}
